import { useEffect, useMemo, useRef, useState } from "react";
import type { InstructionImage, QuoteWhatsApp } from "@probetalab/contracts";
import { get, getBlob, run } from "../lib/api.js";
import { fmtPhone } from "../lib/format.js";
import { downloadBlob, whatsappLink } from "../lib/print.js";
import { composeQuoteMessage, imageFilename, phoneDigits, preselectedImageIds } from "../lib/quoteMessage.js";
import { errorMessage } from "../lib/useQuery.js";
import { copyImageToClipboard, preferShareSheet } from "../lib/imageClipboard.js";
import { shareFiles } from "./WhatsAppSend.js";
import { Alert, Button, Field, Input, Loading, Sheet, Textarea } from "./ui.js";

/**
 * "Enviar por WhatsApp" on a quote (pilot request 2026-11-02): instead of typing the price and the
 * preparation and digging the instruction picture out of the phone's gallery, the counter types
 * (or confirms) the number, reviews the preset message and ticks the instruction images. Same
 * two steps as the results hand-off (WhatsAppSend.tsx), because a web page cannot attach files
 * to a wa.me link:
 *   1. "Abrir chat" opens wa.me with the message, and records the send in the audit chain
 *      (`quote.whatsapp_sent`: the quote and the image ids, never the number or the text).
 *   2. The ticked images go to the chat. On a touch device that can share files (the Android
 *      tablet) "Enviar indicaciones" opens the OS share sheet. On a desktop (Windows + Chrome +
 *      WhatsApp Web, whose share sheet lists only installed apps) each image has «Copiar imagen»
 *      and is pasted with Ctrl + V; if the clipboard refuses, that image is downloaded instead
 *      (drag-and-drop), and «Descargar» is always there as the secondary action.
 * No ticket goes out: the lab does not send one for a WhatsApp quote.
 */
export function QuoteWhatsAppSheet({ open, onClose, quoteId }: { open: boolean; onClose: () => void; quoteId: string }) {
  const [data, setData] = useState<QuoteWhatsApp | null>(null);
  const [images, setImages] = useState<InstructionImage[]>([]);
  const [blobs, setBlobs] = useState<Record<string, Blob>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [chatOpened, setChatOpened] = useState(false);
  const [imagesDone, setImagesDone] = useState<"shared" | "downloaded" | null>(null);
  const [perImage, setPerImage] = useState<Record<string, "copied" | "downloaded">>({});
  const [copying, setCopying] = useState<string | null>(null);
  const shareMode = useMemo(() => preferShareSheet(), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recorded = useRef(false);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setData(null);
    setLoadError(null);
    setChatOpened(false);
    setImagesDone(null);
    setPerImage({});
    setError(null);
    recorded.current = false;
    (async () => {
      try {
        const [wa, list] = await Promise.all([get("quoteWhatsApp", { params: { id: quoteId } }), get("instructionImages")]);
        if (!live) return;
        const active = list.items.filter((i) => i.is_active);
        setData(wa);
        setImages(active);
        setPhone(wa.phone ? fmtPhone(wa.phone) : "");
        setMessage(composeQuoteMessage(wa));
        setSelected(preselectedImageIds(active, wa.lines));
        // The images are fetched now, so the share in step 2 runs inside the tap's user activation.
        const loaded = await Promise.all(
          active.map(async (img) => [img.id, await getBlob("instructionImageFile", { params: { id: img.id } }, img.content_type)] as const),
        );
        if (live) setBlobs(Object.fromEntries(loaded));
      } catch (e) {
        if (live) setLoadError(errorMessage(e));
      }
    })();
    return () => {
      live = false;
    };
  }, [open, quoteId]);

  const previews = useMemo(() => {
    const urls: Record<string, string> = {};
    for (const [id, blob] of Object.entries(blobs)) urls[id] = URL.createObjectURL(blob);
    return urls;
  }, [blobs]);
  useEffect(() => () => Object.values(previews).forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const digits = phoneDigits(phone);
  const chosen = images.filter((img) => selected.includes(img.id));
  const ready = chosen.every((img) => blobs[img.id]);

  async function openChat() {
    if (!digits) return;
    window.open(whatsappLink(digits, message), "_blank", "noopener");
    setChatOpened(true);
    if (recorded.current) return;
    recorded.current = true;
    try {
      await run("quote.whatsapp_sent", { quote_id: quoteId, image_ids: selected });
    } catch (e) {
      recorded.current = false;
      setError(errorMessage(e));
    }
  }

  function files(): File[] {
    return chosen.map((img) => new File([blobs[img.id]!], imageFilename(img.name, img.content_type), { type: img.content_type }));
  }

  function download() {
    for (const f of files()) downloadBlob(f, f.name);
    setImagesDone("downloaded");
  }

  function downloadOne(img: InstructionImage) {
    const blob = blobs[img.id];
    if (!blob) return;
    downloadBlob(blob, imageFilename(img.name, img.content_type));
    setPerImage((p) => ({ ...p, [img.id]: "downloaded" }));
  }

  async function copyOne(img: InstructionImage) {
    const blob = blobs[img.id];
    if (!blob) return;
    setCopying(img.id);
    try {
      if (await copyImageToClipboard(blob)) setPerImage((p) => ({ ...p, [img.id]: "copied" }));
      else downloadOne(img); // refused or unsupported: fall back to the download
    } finally {
      setCopying(null);
    }
  }

  async function sendImages() {
    setBusy(true);
    setError(null);
    try {
      const outcome = await shareFiles(files(), "Indicaciones");
      if (outcome === "cancelled") return;
      if (outcome === "shared") setImagesDone("shared");
      else download();
    } finally {
      setBusy(false);
    }
  }

  const toggle = (id: string) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Enviar por WhatsApp"
      wide
      footer={
        <Button variant={chatOpened ? "primary" : "ghost"} onClick={onClose}>
          {chatOpened ? "Listo" : "Cerrar"}
        </Button>
      }
    >
      {loadError ? (
        <Alert tone="red">{loadError}</Alert>
      ) : !data ? (
        <Loading />
      ) : (
        <>
          <Field label="Número de WhatsApp" hint="10 dígitos." error={phone && !digits ? "Escribe los 10 dígitos del número." : null}>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" placeholder="442 123 4567" className="lg" autoFocus={!data.phone} />
          </Field>
          <Field label="Mensaje" hint="Puedes cambiarlo antes de abrir el chat.">
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} />
          </Field>

          {images.length > 0 && (
            <fieldset className="stack" style={{ gap: 8, border: 0, padding: 0, margin: 0 }}>
              <legend className="label" style={{ marginBottom: 8 }}>
                Indicaciones para enviar
              </legend>
              <div className="row wrap" style={{ gap: 10 }}>
                {images.map((img) => (
                  <label key={img.id} className={`pill${selected.includes(img.id) ? " on" : ""}`} style={{ cursor: "pointer", paddingLeft: 10 }}>
                    <input type="checkbox" checked={selected.includes(img.id)} onChange={() => toggle(img.id)} />
                    {previews[img.id] ? <img src={previews[img.id]} alt="" width={40} height={40} style={{ objectFit: "cover", borderRadius: 6 }} /> : null}
                    <span>{img.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <div className="stack">
            <div className="section-title" style={{ marginBottom: 0 }}>
              <span className="step">1</span>
              <span className="grow">
                <strong>Abre el chat</strong>
                <div className="small muted">Se abre WhatsApp con el mensaje{digits ? ` para ${fmtPhone(digits)}` : ""}. Envíalo.</div>
              </span>
            </div>
            <Button variant={chatOpened ? "secondary" : "primary"} size="lg" block icon={chatOpened ? "check" : "whatsapp"} disabled={!digits || !message.trim()} onClick={() => void openChat()}>
              {chatOpened ? "Chat abierto · abrir de nuevo" : "Abrir chat"}
            </Button>
          </div>

          {chosen.length > 0 && !shareMode && (
            <div className="stack">
              <div className="section-title" style={{ marginBottom: 0 }}>
                <span className="step">2</span>
                <span className="grow">
                  <strong>Copia y pega cada indicación</strong>
                  <div className="small muted">Toca «Copiar imagen» y pégala en el chat. Repite con cada una.</div>
                </span>
              </div>
              {chosen.map((img) => {
                const state = perImage[img.id];
                return (
                  <div key={img.id} className="row" style={{ gap: 10, alignItems: "center" }}>
                    {previews[img.id] ? <img src={previews[img.id]} alt="" width={44} height={44} style={{ objectFit: "cover", borderRadius: 6 }} /> : null}
                    <span className="grow">{img.name}</span>
                    <Button
                      variant={chatOpened && state !== "copied" ? "primary" : "secondary"}
                      style={{ minHeight: 44 }}
                      busy={copying === img.id || !blobs[img.id]}
                      onClick={() => void copyOne(img)}
                    >
                      {state === "copied" ? "Copiada ✓" : "Copiar imagen"}
                    </Button>
                    <Button variant="ghost" size="sm" icon="download" style={{ minHeight: 44 }} disabled={!blobs[img.id]} onClick={() => downloadOne(img)}>
                      Descargar
                    </Button>
                  </div>
                );
              })}
              {chosen.some((i) => perImage[i.id] === "downloaded") && (
                <Alert tone="blue">Se descargó la imagen. Arrástrala al chat de WhatsApp.</Alert>
              )}
              {chatOpened && <div className="small muted">En el chat de WhatsApp presiona Ctrl + V para pegarla.</div>}
            </div>
          )}

          {chosen.length > 0 && shareMode && (
            <div className="stack">
              <div className="section-title" style={{ marginBottom: 0 }}>
                <span className="step">2</span>
                <span className="grow">
                  <strong>Envía las indicaciones</strong>
                  <div className="small muted">Elige WhatsApp y el mismo chat, que ya aparece primero en la lista.</div>
                </span>
              </div>
              <Button
                variant={chatOpened && !imagesDone ? "primary" : "secondary"}
                size="lg"
                block
                icon={imagesDone ? "check" : "send"}
                busy={busy || !ready}
                disabled={!ready}
                onClick={() => void sendImages()}
              >
                {imagesDone === "shared" ? "Indicaciones enviadas · enviar de nuevo" : chosen.length === 1 ? "Enviar indicaciones" : `Enviar ${chosen.length} indicaciones`}
              </Button>
              {imagesDone === "downloaded" ? (
                <Alert tone="blue">
                  Se {chosen.length === 1 ? "descargó la imagen" : `descargaron ${chosen.length} imágenes`} ({chosen.map((i) => i.name).join(", ")}). Arrástralas al chat de WhatsApp, o toca el clip → Fotos.
                </Alert>
              ) : (
                <Button variant="ghost" size="sm" icon="download" disabled={!ready} onClick={download}>
                  ¿No aparece WhatsApp? Descargar las imágenes
                </Button>
              )}
            </div>
          )}

          {error && <Alert tone="red">{error}</Alert>}
        </>
      )}
    </Sheet>
  );
}
